import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PolicyForm from '../form/PolicyForm';
import { useHistory } from 'react-router-dom';
import CommentsList from '../../Comment/list/CommentsList';
import PolicyCard from '../card/PolicyCard';
import ListHeader from '../../../common/ui/components/ListHeader/ListHeader';
import { getMeetingPolicies, getPolicy, getPolicies, deletePolicy, getPoliciesAcl } from '../api';
import DeleteConfirmation from '../../../common/ui/components/DeleteConfirmation/DeleteConfirmation';
import Pagination from '../../../common/ui/components/Pagination/Pagination';
import '../styles.scss';

const PolicyList = forwardRef((props, ref) => {
    const { meetingId, openForm, policyId, showFormOptions, match } = props;

    const [policies, setPolicies] = useState([]);
    const [policiesCount, setPoliciesCount] = useState(0);
    const [defaultPage, setDefaultPage] = useState(1);
    const [policiesAcl, setPoliciesAcl] = useState({});
    const [shouldShowForm, setShouldShowForm] = useState(false);
    const [policyToEdit, setPolicyToEdit] = useState(policyId);
    const [focusedPolicyId, setFocusedPolicyId] = useState(match ? match.params.policyId : null);
    const [commentMentionedList, setCommentMentionedList] = useState([]);
    const [policyIdToDelete, setPolicyIdToDelete] = useState(null);
    const [isDeleteConfirmationShowing, setIsDeleteConfirmationShowing] = useState(false);

    const history = useHistory();

    useEffect(() => {
        handlePageChange(1);
        const fetchPoliciesAcl = async () => {
            if (!meetingId) {
                return;
            }
            setPoliciesAcl(await getPoliciesAcl(meetingId));
        };

        if (openForm) handleToggleForm();
        fetchPoliciesAcl();
    }, [meetingId]);
    useEffect(() => {
        setFocusedPolicyId(match ? match.params.policyId : null);
    }, [history.location.pathname]);
    useEffect(() => {
        const fetchFocusedPolicy = async () => {
            if (!focusedPolicyId) return;
            const fetchedPolicy = await getPolicy(focusedPolicyId);
            if (!fetchedPolicy) return;
            handlePageChange(fetchedPolicy.pageNumber);
            setCommentMentionedList(fetchedPolicy ? fetchedPolicy.participants : []);
        };
        fetchFocusedPolicy();
    }, [focusedPolicyId]);

    useImperativeHandle(ref, () => ({
        handlePublishSummaryFormPolicy() {
            setPoliciesAcl({ create: false, edit: false, delete: false });
        },
        handleRevertSummaryFormPolicy() {
            setPoliciesAcl({ create: true, edit: true, delete: true });
        },
    }));

    const handlePageChange = pageNumber => {
        setDefaultPage(null);
        setDefaultPage(pageNumber);

        const fetchPolicies = async () => {
            const data = meetingId ? await getMeetingPolicies(meetingId) : await getPolicies(pageNumber);
            setPoliciesCount(meetingId ? 0 : data.count);
            setPolicies(meetingId ? data : data.policies);
        };
        fetchPolicies();
    };
    const handleToggleForm = () => {
        setShouldShowForm(!shouldShowForm);
    };
    const handleAddPolicy = policy => {
        setShouldShowForm(false);
        setPolicies(policies => [policy, ...policies]);
    };
    const handleUpdatePolicy = updatedPolicy => {
        setShouldShowForm(false);
        const indexOfUpdatedPolicy = policies.findIndex(policy => policy._id === updatedPolicy._id);
        const updatedPolicies = [...policies];
        updatedPolicies[indexOfUpdatedPolicy] = updatedPolicy;
        setPolicies(updatedPolicies);
        setPolicyToEdit(null);
    };
    const handleEditPolicy = policy => {
        setPolicyToEdit(policy);
        setShouldShowForm(true);
    };
    const handleDeletePolicy = async () => {
        if (!policyIdToDelete) return;
        await deletePolicy(meetingId, policyIdToDelete);
        const remainingPolicies = policies.filter(policy => {
            return policy._id !== policyIdToDelete;
        });

        setPolicies(remainingPolicies);
        setIsDeleteConfirmationShowing(false);
    };
    const handleToggleComments = policyId => {
        if (focusedPolicyId === policyId || !policyId) {
            history.push(meetingId ? '../' : '../policies');
            return;
        }
        setFocusedPolicyId(policyId);
        history.push(meetingId ? `/meetings/${meetingId}/policies/${policyId}` : `/policies/${policyId}`);
    };

    return (
        <div className="comments-sidebar-layout list-layout">
            {match && match.params.policyId && (
                <div onClick={() => handleToggleComments()} className="unfocused-background" />
            )}

            <div className={`content ${focusedPolicyId ? 'focused' : ''}`}>
                <div className={`list ${focusedPolicyId && 'remove-list-margin'}`}>
                    <div className="summary">
                        {shouldShowForm ? (
                            <PolicyForm
                                policyToEdit={policyToEdit}
                                handleCancel={() => {
                                    handleToggleForm();
                                    setPolicyToEdit(null);
                                }}
                                handleAddPolicy={handleAddPolicy}
                                handleUpdatePolicy={handleUpdatePolicy}
                                meetingId={meetingId}
                            />
                        ) : (
                            <>
                                <ListHeader
                                    listTitle="تصمیمات"
                                    handleOpenForm={showFormOptions ? handleToggleForm : null}
                                />
                                {policies.length > 0 ? (
                                    <>
                                        {policies.map((policy, i) => (
                                            <PolicyCard
                                                key={`policy-${i}`}
                                                isMeetingProfileToggled={Boolean(meetingId)}
                                                policy={policy}
                                                focusedPolicyId={focusedPolicyId}
                                                handleToggleComments={handleToggleComments}
                                                handleEditPolicy={handleEditPolicy}
                                                handleDeletePolicy={() => {
                                                    setPolicyIdToDelete(policy._id);
                                                    setIsDeleteConfirmationShowing(true);
                                                }}
                                                acl={meetingId ? policiesAcl : null}
                                            />
                                        ))}
                                        {!meetingId && defaultPage && (
                                            <Pagination
                                                count={policiesCount}
                                                defaultPage={defaultPage}
                                                onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                                            />
                                        )}
                                        <DeleteConfirmation
                                            onClose={() => {
                                                setIsDeleteConfirmationShowing(false);
                                            }}
                                            onClickOk={() => {
                                                handleDeletePolicy();
                                            }}
                                            isOpen={isDeleteConfirmationShowing}
                                        />
                                    </>
                                ) : (
                                    'اطلاعاتی وجود ندارد'
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {focusedPolicyId && (
                <div className="comments-sidebar">
                    <CommentsList mentionedList={commentMentionedList} />
                </div>
            )}
        </div>
    );
});

export default PolicyList;
